import { TerminalConnectionStatus } from '../models/terminal-status.model';

/**
 * How terminal liveness is presented, in one place.
 *
 * Both the platform-wide Terminals screen and the terminals panel on a business profile render
 * the same terminals, so the label and colour for a given state have to come from here — a screen
 * calling a terminal "Offline" in red while another calls it "Inactive" in grey makes both
 * untrustworthy.
 *
 * These cover CONNECTIVITY only. Whether a terminal is enabled (`isActive`) and whether an
 * operator deliberately took it dark (`offlineMode`) are separate facts rendered as their own
 * flags, never folded into this one.
 */

export function terminalStatusLabel(status: TerminalConnectionStatus): string {
  if (status === 'never') return 'Never connected';
  return status === 'online' ? 'Online' : 'Offline';
}

export function terminalStatusClass(status: TerminalConnectionStatus): string {
  if (status === 'online') return 'pill pill-sm pill-green';
  if (status === 'offline') return 'pill pill-sm pill-red';
  return 'pill pill-sm pill-grey';
}

/**
 * How long ago the terminal was last heard from, in the coarsest unit that still says something
 * useful. Returns an empty string when there is nothing to age.
 *
 * `asOf` must be the server's clock, not the browser's: the status beside this age was decided
 * server-side, and measuring against a skewed local clock is how a row ends up reading
 * "2m ago" next to an Offline pill.
 */
export function terminalLastSeenAge(lastSeenUtc: string | null, asOf: Date | null): string {
  if (!lastSeenUtc || !asOf) return '';

  const elapsedSeconds = Math.round((asOf.getTime() - new Date(lastSeenUtc).getTime()) / 1000);
  if (elapsedSeconds < 0) return 'just now';
  if (elapsedSeconds < 60) return `${elapsedSeconds}s ago`;

  const minutes = Math.floor(elapsedSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  return `${Math.floor(hours / 24)}d ago`;
}
